import type { JSX } from 'react';
import { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Pagination,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { useQuery } from '@tanstack/react-query';

import { vkBotApi, type VkBotFeedItem } from '../lib/vkBotApi';
import { AddExampleModal } from './TrainingPage';

const STATUS_META: Record<string, { color: string; label: string }> = {
  pending: { color: 'default', label: 'в очереди' },
  processing: { color: 'processing', label: 'обрабатывается' },
  replied: { color: 'green', label: 'отвечено' },
  skipped: { color: 'default', label: 'пропущено' },
  failed: { color: 'red', label: 'ошибка' },
};

const SKIP_LABELS: Record<string, string> = {
  self: 'свой комментарий',
  group: 'от сообщества',
  stop_word: 'стоп-слово',
  limit_user: 'лимит на человека',
  limit_post: 'лимит на пост',
  llm_declined: 'бот решил промолчать',
  contest: 'розыгрыш — бот молчит',
  empty: 'пустой текст',
  disabled: 'бот выключен',
  invalid_json: 'LLM вернул мусор',
  llm_unavailable: 'LLM недоступен',
};

const PAGE_SIZE = 30;

/** Лента «комментарий → ответ бота» со статусами и отладкой. */
export function FeedPage(): JSX.Element {
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [groupId, setGroupId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [trainItem, setTrainItem] = useState<VkBotFeedItem | null>(null);

  const groupsQuery = useQuery({
    queryKey: ['vk-bot', 'groups'],
    queryFn: () => vkBotApi.listGroups(),
  });
  const feedQuery = useQuery({
    queryKey: ['vk-bot', 'feed', { status, groupId, page }],
    queryFn: () =>
      vkBotApi.feed({
        status,
        groupId,
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      }),
    refetchInterval: 10_000,
  });

  const items = feedQuery.data?.items ?? [];

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Лента ответов
        </Typography.Title>
        <Space>
          <Select
            allowClear
            placeholder="Все группы"
            style={{ minWidth: 220 }}
            value={groupId}
            onChange={(v) => {
              setGroupId(v);
              setPage(1);
            }}
            options={(groupsQuery.data ?? []).map((g) => ({
              value: g.id,
              label: g.label,
            }))}
          />
          <Select
            allowClear
            placeholder="Все статусы"
            style={{ width: 180 }}
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={Object.entries(STATUS_META).map(([v, m]) => ({
              value: v,
              label: m.label,
            }))}
          />
        </Space>
      </Space>

      {feedQuery.isError && (
        <Alert type="error" showIcon message="Не удалось загрузить ленту" />
      )}

      {items.length === 0 && !feedQuery.isLoading && (
        <Card>
          <Typography.Text type="secondary">
            Пока пусто. Как только под постом подключённой группы появится
            комментарий — он попадёт сюда.
          </Typography.Text>
        </Card>
      )}

      {items.map((item) => (
        <FeedCard key={item.id} item={item} onTrain={() => setTrainItem(item)} />
      ))}

      <AddExampleModal
        open={trainItem !== null}
        onClose={() => setTrainItem(null)}
        groups={(groupsQuery.data ?? []).map((g) => ({ value: g.id, label: g.label }))}
        initial={
          trainItem
            ? {
                question: trainItem.text,
                badReply: trainItem.replyText ?? '',
                groupId: null,
              }
            : undefined
        }
      />

      {(feedQuery.data?.total ?? 0) > PAGE_SIZE && (
        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={feedQuery.data?.total ?? 0}
          onChange={setPage}
          showSizeChanger={false}
        />
      )}
    </Space>
  );
}

function FeedCard({
  item,
  onTrain,
}: {
  item: VkBotFeedItem;
  onTrain: () => void;
}): JSX.Element {
  const meta = STATUS_META[item.status] ?? { color: 'default', label: item.status };
  return (
    <Card size="small" styles={{ body: { padding: 16 } }}>
      <Space direction="vertical" size={8} style={{ width: '100%' }}>
        <Space size={8} wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space size={8} wrap>
            <Tag color={meta.color}>{meta.label}</Tag>
            {item.skipReason && (
              <Tag>{SKIP_LABELS[item.skipReason] ?? item.skipReason}</Tag>
            )}
            {item.llmProvider && <Tag color="blue">{item.llmProvider}</Tag>}
            {item.isTest && <Tag color="purple">тест</Tag>}
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {item.groupLabel} · {new Date(item.createdAt).toLocaleString('ru')}
            </Typography.Text>
            {!item.isTest && (
              <a href={item.commentUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                открыть в ВК ↗
              </a>
            )}
          </Space>
          <Button size="small" onClick={onTrain}>
            🎓 Обучить
          </Button>
        </Space>
        {item.postText ? (
          <Typography.Paragraph
            type="secondary"
            style={{ fontSize: 12, margin: 0, background: '#fafafa', borderRadius: 6, padding: '6px 10px' }}
            ellipsis={{ rows: 2, expandable: true, symbol: 'ещё' }}
          >
            📌 Пост: {item.postText}
          </Typography.Paragraph>
        ) : (
          !item.isTest && (
            <Typography.Text type="warning" style={{ fontSize: 12 }}>
              ⚠️ Пост не прочитан — бот отвечал без контекста поста
            </Typography.Text>
          )
        )}
        <div>
          <Typography.Text strong>
            {item.fromName ?? `Клиент ${item.fromId}`}:
          </Typography.Text>{' '}
          <Typography.Text>{item.text || '(без текста)'}</Typography.Text>
        </div>
        {item.replyText && (
          <div
            style={{
              background: '#f0f7ff',
              borderRadius: 8,
              padding: '8px 12px',
            }}
          >
            <Typography.Text style={{ fontSize: 13 }}>
              🤖 {item.replyText}
            </Typography.Text>
          </div>
        )}
        {item.addressExtracted && (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Адрес: «{item.addressExtracted}»
            {item.geoResult?.zoneName
              ? ` → зона «${item.geoResult.zoneName}», мин. ${item.geoResult.minOrder} ₽`
              : item.geoResult?.imprecise
                ? ' → нужен номер дома'
                : item.geoResult
                  ? ' → вне зон'
                  : ''}
          </Typography.Text>
        )}
        {item.errorText && (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            {item.errorText}
          </Typography.Text>
        )}
      </Space>
    </Card>
  );
}
